import React, { useEffect, useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import uuid from 'react-uuid';
import PropTypes from 'prop-types';
import { BalanceContext, ChildContext } from '../context';
import { encryptPayload } from '../../utils/util';
import QrCode from './Qrcode';
import CurrencyInput from './CurrencyInput';

/*
Returns:
1. RADIO BUTTONS with balances of the selected card info
[4 opt - outstanding balances, last billed due, minimum due and other amount]
(on selection of other amt. option, input box displays)
2. CURRENCY INPUT box to add custom amount [using currencyInput.jsx]
3. QRCODE based on the selection [using qrcode.jsx]
*/
const QrWrapper = (props) => {
  const {
    fieldOptions,
    memberInfo,
    fieldLabel,
    fieldId,
    cssProperties,
    fieldName,
    fieldValidation,
    invalidBalanceMessage,
    propsForOtherAmount,
    listNodes = [],
    handleOnChangeEvents,
    setFormSubmissionPayload,
    formSubmissionPayload,
  } = props;

  const balanceContextVal = useContext(BalanceContext);
  const [options, setOptions] = useState([...fieldOptions]);
  const contextVal = useContext(ChildContext);
  const [amtPending, setAmtPending] = useState(null);
  const [currentOption, setCurrentOption] = useState();
  const isDisabled = false;
  const memberApiUrls = useSelector((state) => state.getIn(['config', 'memberApiUrls']));
  const market = contextVal.cuid.split('_')[2];
  /**
   * Call API to get Balance details and UPI Id using Account token on
   * every selection of new card number from dropdown. (Update balanceContextVal)
   */
  const fetchBalances = () => {
    const accToken = memberInfo.accountToken;
    const payload = {
      head: {
        traceId: uuid(),
        span: 'span',
      },
      body: {
        account_token: accToken,
        request_type: 'UI',
        // splitting cuid: qrcode_en_IN => [qrcode, en, IN] and fetching IN, the last index of array
        market,
      },
    };

    fetch(`${memberApiUrls}/iForms/secure/balance?cuid=${contextVal.cuid}`, {
      defer: true,
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        traceId: uuid(),
      },
      body: encryptPayload(payload),
    })
      .then((response) => response.json())
      .then((data) => {
        balanceContextVal.setBalanceDetails(data.balance_summary);
        balanceContextVal.setUpiId(data.upi_id);
      });
  };
  // get amounts to add in field options using context
  const ChangeVal = () => {
    let currencySymbol = '₹';
    if (market === 'IN') currencySymbol = '₹';
    else if (market === 'SG') currencySymbol = '$';
    const currentOutstanding = balanceContextVal.balanceDetails
      && balanceContextVal.balanceDetails.current_outstanding
      ? balanceContextVal.balanceDetails.current_outstanding
      : '0.00';
    const lastBillDue = balanceContextVal.balanceDetails
      && balanceContextVal.balanceDetails.last_bill_due
      ? balanceContextVal.balanceDetails.last_bill_due
      : '0.00';
    const minimumDue = balanceContextVal.balanceDetails
      && balanceContextVal.balanceDetails.minimum_due
      ? balanceContextVal.balanceDetails.minimum_due
      : '0.00';

    // add the respective amounts in field options
    const newOptions = options.map((element, index) => {
      const newElement = { ...element };
      switch (element.fieldId) {
        case 'current_outstanding':
          if (currentOutstanding) {
            const label = `${fieldOptions[index].fieldLabel} ${currencySymbol} ${currentOutstanding}`;
            newElement.fieldLabel = label;
            newElement.value = currentOutstanding;
          }
          break;
        case 'last_billed_due':
          if (lastBillDue) {
            const label = `${fieldOptions[index].fieldLabel} ${currencySymbol} ${lastBillDue}`;
            newElement.fieldLabel = label;
            newElement.value = lastBillDue;
          }
          break;
        case 'minimum_due':
          if (minimumDue) {
            const label = `${fieldOptions[index].fieldLabel} ${currencySymbol} ${minimumDue}`;
            newElement.fieldLabel = label;
            newElement.value = minimumDue;
          }
          break;
        default:
      }
      return newElement;
    });
    setOptions([...newOptions]);
  };

  // if card selection changes (memberInfo), fetch new balances and account token
  useEffect(() => {
    // calling api to get outstanding details of customer using account token
    fetchBalances();
  }, [memberInfo]);

  // when balances changes =>
  // set amount to null, deselect the radio button, changes balance details on UI
  useEffect(() => {
    setAmtPending(null);
    if (currentOption) {
      const lastChecked = document.querySelector(`#${currentOption}`);
      lastChecked.checked = false;
      setCurrentOption(null);
    }
    if (balanceContextVal.balanceDetails) {
      ChangeVal();
    }
  }, [balanceContextVal]);

  // Update the pending amount in payload whenever there's change
  useEffect(() => {
    setFormSubmissionPayload({ ...formSubmissionPayload, amtPending });
  }, [amtPending]);

  const handleOnChange = (option) => {
    setAmtPending(option.value);
    setCurrentOption(option.fieldId);
  };
  const getOtherPayValue = (amt) => {
    amt = amt.replace(/,/g, '');
    setAmtPending(amt);
  };

  // make a radio button with newly created options
  return (
    <div className="col-md-12 pad-4-b">
      {!isDisabled && options && (
        <div className={cssProperties.style}>
          <fieldset>
            <label
              htmlFor={fieldId}
              className={`${fieldLabel.cssProperties.style} margin-2-b`}
            >
              {fieldLabel.label}
            </label>
            <span> </span>
            {!fieldValidation.required && <span> (Optional)</span>}
            <div className="row">
              {options.map((option) => (
                <div className="col-md-12 radio pad-4-r" key={option.fieldId}>
                  <input
                    id={option.fieldId}
                    type="radio"
                    name={fieldName}
                    value={option.value}
                    onChange={() => handleOnChange(option)}
                    required={fieldValidation.required}
                    disabled={isDisabled}
                  />
                  <label
                    htmlFor={option.fieldId}
                    className={fieldLabel.cssProperties.style}
                  >
                    {option.fieldLabel}
                  </label>
                </div>
              ))}
            </div>
          </fieldset>
        </div>
      )}
      {!isDisabled && currentOption === 'other_amt' && (
        <CurrencyInput
          {...propsForOtherAmount}
          getValue={getOtherPayValue}
          handleOnChangeEvents={handleOnChangeEvents}
        />
      )}
      {amtPending > 0 && <QrCode amtPay={amtPending} market={market} listNodes={listNodes} />}
      {invalidBalanceMessage && amtPending && amtPending <= 0
        && (
          <div className={invalidBalanceMessage.cssProperties}>
            {invalidBalanceMessage.message}
          </div>
        )}
    </div>
  );
};

QrWrapper.propTypes = {
  fieldLabel: PropTypes.objectOf(PropTypes.shape),
  fieldId: PropTypes.string,
  fieldName: PropTypes.string,
  fieldOptions: PropTypes.objectOf(PropTypes.shape),
  cssProperties: PropTypes.objectOf(PropTypes.shape),
  memberInfo: PropTypes.objectOf(PropTypes.shape),
  fieldValidation: PropTypes.objectOf(PropTypes.shape),
  propsForOtherAmount: PropTypes.objectOf(PropTypes.shape),
  handleOnChangeEvents: PropTypes.func,
  invalidBalanceMessage: PropTypes.objectOf(PropTypes.shape),
  setFormSubmissionPayload: PropTypes.func,
  formSubmissionPayload: PropTypes.objectOf(PropTypes.shape),
  listNodes: PropTypes.objectOf(PropTypes.shape),
};

export default QrWrapper;
